/**
 * useAdminBackgrounds — Carga fondos virtuales subidos por el admin.
 *
 * Retorna un array de BackgroundOption compatible con el picker existente,
 * combinando los fondos estáticos del catálogo con los subidos por el admin.
 */

import { useEffect, useState } from 'react';
import type { BackgroundOption } from './backgroundCatalog';
import { BACKGROUND_CATALOG } from './backgroundCatalog';
import { getAdminBackgrounds, type AdminBackground } from '../services/backgroundService';

/**
 * Convierte un AdminBackground (Firestore) a un BackgroundOption
 * compatible con VirtualBgApplier.
 */
function toBackgroundOption(bg: AdminBackground): BackgroundOption {
  return {
    id: `admin_${bg.id}`,
    label: bg.name,
    kind: 'image',
    // No generator — se usa directamente la URL remota
    preview: `url(${bg.downloadUrl})`,
    // Campo extra para que getBackgroundImageUrl sepa que es URL remota
    remoteUrl: bg.downloadUrl,
  };
}

export interface UseAdminBackgroundsResult {
  backgrounds: BackgroundOption[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAdminBackgrounds(): UseAdminBackgroundsResult {
  const [adminBgs, setAdminBgs] = useState<AdminBackground[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const bgs = await getAdminBackgrounds();
        if (!cancelled) setAdminBgs(bgs.filter((b) => b.active));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error cargando fondos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Merge: static catalog first, then admin backgrounds
  const backgrounds: BackgroundOption[] = [
    ...BACKGROUND_CATALOG,
    ...adminBgs.map(toBackgroundOption),
  ];

  return {
    backgrounds,
    loading,
    error,
    refresh: () => setRefreshKey((k) => k + 1),
  };
}
